import React, { useState, useEffect } from "react";
import { fetchUsersByRole } from "../../../api";
import { USER_ROLES } from "../../constants";
import { FaUserFriends } from "react-icons/fa";
import { IoSearchOutline } from "react-icons/io5";
import "../PatientsList/PatientsList.css";

const InstitutionOwnerList = ({ selectedInstitutionOwnerId, onInstitutionOwnerClick }) => {
  const [institutionOwners, setInstitutionOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  const loadInstitutionOwners = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchUsersByRole(USER_ROLES.INSTITUTION_OWNER);
      // Handle different response structures
      const ownersList = Array.isArray(response) 
        ? response 
        : (response?.users || response?.data || []);
      setInstitutionOwners(ownersList);
    } catch (error) {
      console.error("Error loading institution owners:", error);
      setError("Failed to load institution owners");
      setInstitutionOwners([]); // Ensure institutionOwners is always an array
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInstitutionOwners();
  }, []);

  const handleInstitutionOwnerClick = (institutionOwner) => {
    if (onInstitutionOwnerClick) {
      onInstitutionOwnerClick(institutionOwner);
    }
  };

  const getAllInstitutionOwners = () => {
    return institutionOwners || [];
  };

  const getFilteredInstitutionOwners = () => {
    let filteredInstitutionOwners = getAllInstitutionOwners();

    if (searchInput) {
      filteredInstitutionOwners = filteredInstitutionOwners.filter(
        (institutionOwner) =>
          institutionOwner.full_name
            ?.toLowerCase()
            .includes(searchInput.toLowerCase()) ||
          institutionOwner.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
          institutionOwner.username?.toLowerCase().includes(searchInput.toLowerCase())
      );
    }

    return filteredInstitutionOwners;
  };

  const getFilteredCounts = () => {
    const allInstitutionOwners = institutionOwners || [];

    const filteredAll = searchInput
      ? allInstitutionOwners.filter(
          (institutionOwner) =>
            institutionOwner.full_name
              ?.toLowerCase()
              .includes(searchInput.toLowerCase()) ||
            institutionOwner.email?.toLowerCase().includes(searchInput.toLowerCase()) ||
            institutionOwner.username?.toLowerCase().includes(searchInput.toLowerCase())
        )
      : allInstitutionOwners;

    return {
      total: filteredAll.length,
      active: 0,
      inactive: 0,
    };
  };



  if (loading) {
    return (
      <div className="patients-container">
        <div className="loading-message">Loading Institution Owners...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patients-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadInstitutionOwners}>Retry</button>
        </div>
      </div>
    );
  }

  const filteredInstitutionOwners = getFilteredInstitutionOwners();
  const filteredCounts = getFilteredCounts();

  return (
    <div className="patients-container">
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-wrapper first">
            <FaUserFriends className="stat-icon" />
          </div>
          <div className="stat-content">
            <span className="stat-label">
              Total Institution Owners {searchInput && "(filtered)"}
            </span>
            <span className="stat-value">{filteredCounts.total}</span>
          </div>
        </div>
      </div>

      <div className="patients-table-container">
        <div className="table-header">
          <div className="table-controls">
            <div className="search-container">
              <IoSearchOutline className="search-icon" />
              <input
                placeholder="Search Institution Owners..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="patients-table">
          <div className="table-header-row four-columns">
            <span className="header-cell">Name</span>
            <span className="header-cell">Email</span>
            <span className="header-cell">Organization</span>
            <span className="header-cell">Actions</span>
          </div>

          {filteredInstitutionOwners.map((institutionOwner, index) => (
            <div key={institutionOwner.username || institutionOwner.email || index} className="table-row four-columns">
              <span className="table-cell patient-name">
                {institutionOwner.full_name}
              </span>
              <span className="table-cell">{institutionOwner.email || 'N/A'}</span>
              <span className="table-cell">{institutionOwner.metadata?.organizationName || 'N/A'}</span>
              <span className="table-cell">
                <button
                  className="action-button"
                  onClick={() => handleInstitutionOwnerClick(institutionOwner)}
                >
                  View
                </button>
              </span>
            </div>
          ))}

          {filteredInstitutionOwners.length === 0 && (
            <div className="no-data-row">
              <span className="no-data-message">
                {searchInput
                  ? "No Institution Owners found matching your search."
                  : "No Institution Owners available."}
              </span>
            </div>
          )}
        </div>

        <div className="table-footer">
          <span className="table-info">
            Showing {filteredInstitutionOwners.length} of {filteredCounts.total} Institution Owners
          </span>
        </div>
      </div>
    </div>
  );
};

export default InstitutionOwnerList;
